import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Send, Video, Clock, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

function fmtSec(s) {
  s = Math.max(0, s | 0);
  const m = Math.floor(s / 60), r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

export default function ConsultRoomPage() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sender, setSender] = useState("user"); // user | doctor
  const [notFound, setNotFound] = useState(false);
  // Doctor toggle is only visible when the doctor unlocked this device with the admin key
  // (they open it via /consult-admin which stores the admin key in sessionStorage).
  const isDoctorDevice = typeof window !== "undefined" &&
    Boolean(sessionStorage.getItem("consult-admin-key"));
  const [sending, setSending] = useState(false);
  const [loadingBooking, setLoadingBooking] = useState(true);
  const listRef = useRef(null);

  const loadBooking = useCallback(async () => {
    try {
      setBooking(await api.consultGet(bookingId));
      setNotFound(false);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error(e?.response?.data?.detail || "Booking lookup failed");
    }
    setLoadingBooking(false);
  }, [bookingId]);

  const loadMessages = useCallback(async () => {
    try { setMessages(await api.consultMessages(bookingId)); } catch { /* ignore */ }
  }, [bookingId]);

  useEffect(() => {
    loadBooking();
    loadMessages();
    const tickBooking = setInterval(() => {
      // stop polling if the booking id was invalid
      if (notFound) return;
      loadBooking();
    }, 5000);
    const tickMsgs = setInterval(() => {
      if (notFound) return;
      loadMessages();
    }, 3000);
    return () => { clearInterval(tickBooking); clearInterval(tickMsgs); };
  }, [loadBooking, loadMessages, notFound]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const adminKey = sender === "doctor" ? sessionStorage.getItem("consult-admin-key") : null;
      await api.consultSend(bookingId, sender, t, adminKey);
      setText("");
      loadMessages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  if (loadingBooking) {
    return <FullScreenMsg msg="Loading your consultation…" />;
  }
  if (notFound || !booking) {
    return <FullScreenMsg msg="Booking not found." />;
  }

  const paid = booking.status === "paid";
  const live = booking.live;
  const expired = booking.expired;

  return (
    <div className="min-h-screen bg-[#0F1115] text-[#E6E1D6] flex flex-col">
      <div className="grain-overlay pointer-events-none fixed inset-0 z-0" />

      <header className="relative z-10 border-b border-[#2A2D35] bg-[#0F1115]/90 backdrop-blur-md sticky top-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 text-[#A19E95] hover:text-[#D4AF37]" data-testid="room-back-link">
            <ChevronLeft size={18} />
            <div>
              <div className="text-[9px] uppercase tracking-[0.4em] text-[#6E6B64] font-mono">Back</div>
              <div className="font-heading text-base">Insight Engine</div>
            </div>
          </Link>
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-[0.4em] text-[#D4AF37] font-mono">Consult Room</div>
            <div className="text-[10px] text-[#A8896C] font-mono">{booking.booking_id}</div>
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 py-4 sm:py-6 flex flex-col gap-4">
        {/* Status bar */}
        <div className="border border-[#2A2D35] bg-[#181A1F]/60 p-4 sm:p-5 flex flex-wrap items-center justify-between gap-4"
             data-testid="room-status">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A19E95] font-mono">Slot</div>
            <div className="font-heading text-lg text-[#E6E1D6]">
              {new Date(booking.slot_start).toLocaleString()} — {new Date(booking.slot_end).toLocaleTimeString()}
            </div>
            <div className="text-[10px] text-[#6E6B64] font-mono mt-1">
              {booking.minutes} min · ₹{booking.amount_inr} · {booking.mode === "call" ? "Voice/Video" : "Text chat"}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <StatusBadge paid={paid} live={live} expired={expired}
                         secondsToStart={booking.seconds_to_start}
                         secondsLeft={booking.seconds_left} />
            {paid && !expired && (
              <a href={booking.jitsi_url} target="_blank" rel="noopener noreferrer"
                 data-testid="room-jitsi-btn"
                 className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 px-3 py-2 text-xs font-mono uppercase tracking-widest">
                <Video size={14} /> Join Voice/Video <ExternalLink size={12} />
              </a>
            )}
          </div>
        </div>

        {/* Chat window */}
        <div className="border border-[#2A2D35] bg-[#181A1F]/60 flex-1 flex flex-col min-h-[400px]">
          <div ref={listRef} data-testid="room-messages"
               className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3 scrollbar-thin">
            {messages.length === 0 && (
              <div className="text-[#6E6B64] font-mono text-xs text-center py-8">
                {live ? "Say hello to start the conversation."
                      : paid ? "Chat opens at your slot start-time."
                             : "Waiting for payment confirmation…"}
              </div>
            )}
            {messages.map((m) => (
              <div key={m.message_id}
                   className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] px-3 py-2 border text-sm font-light leading-relaxed
                    ${m.sender === "user"
                      ? "border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#F0E7C8]"
                      : "border-[#2A2D35] bg-[#0F1115] text-[#E6E1D6]"}`}>
                  <div className="text-[9px] uppercase tracking-widest font-mono opacity-60 mb-1">
                    {m.sender === "user" ? booking.name : "Dr. Tejakumar"} · {new Date(m.ts).toLocaleTimeString()}
                  </div>
                  <div className="whitespace-pre-wrap">{m.text}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-[#2A2D35] p-3 sm:p-4">
            {!paid && (
              <div className="text-xs text-[#A19E95] font-mono">Payment pending. Refresh after paying.</div>
            )}
            {paid && expired && (
              <div className="text-xs text-[#A19E95] font-mono" data-testid="room-expired-msg">
                This slot has ended. Book another slot to continue.
              </div>
            )}
            {paid && !expired && !live && (
              <div className="text-xs text-[#A19E95] font-mono">
                Chat opens in <span className="text-[#D4AF37]">{fmtSec(booking.seconds_to_start)}</span>.
              </div>
            )}
            {paid && live && (
              <div className="flex items-end gap-2">
                {isDoctorDevice ? (
                  <select value={sender} onChange={e => setSender(e.target.value)}
                          data-testid="room-sender-select"
                          className="bg-[#0F1115] border border-[#2A2D35] text-[#D4AF37] font-mono text-xs px-2 py-2">
                    <option value="user">Patient</option>
                    <option value="doctor">Doctor</option>
                  </select>
                ) : (
                  <span className="text-[9px] uppercase tracking-widest font-mono text-[#6E6B64] px-2 py-2">
                    You
                  </span>
                )}
                <textarea data-testid="room-text-input" value={text}
                          onChange={e => setText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                          rows={2} placeholder="Type a message…"
                          className="flex-1 bg-[#0F1115] border border-[#2A2D35] text-[#E6E1D6] px-3 py-2 text-sm font-light resize-none focus:border-[#D4AF37] outline-none" />
                <Button data-testid="room-send-btn" onClick={send} disabled={sending || !text.trim()}
                        className="rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158] h-full">
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function StatusBadge({ paid, live, expired, secondsToStart, secondsLeft }) {
  let label = "Pending", color = "border-[#A19E95] text-[#A19E95]";
  if (paid && live) { label = `LIVE · ${fmtSec(secondsLeft)}`; color = "border-[#2ecc71] text-[#2ecc71]"; }
  else if (paid && expired) { label = "Ended"; color = "border-[#6E6B64] text-[#6E6B64]"; }
  else if (paid) { label = `Starts in ${fmtSec(secondsToStart)}`; color = "border-[#D4AF37] text-[#D4AF37]"; }
  return (
    <span className={`inline-flex items-center gap-1 border px-2 py-1 text-[10px] uppercase tracking-widest font-mono ${color}`}
          data-testid="room-status-badge">
      <Clock size={11} /> {label}
    </span>
  );
}

function FullScreenMsg({ msg }) {
  return (
    <div className="min-h-screen bg-[#0F1115] text-[#A19E95] font-mono flex items-center justify-center px-4">
      {msg}
    </div>
  );
}
